'use client'

import { Button } from '@/components/ui/button'
import { Check, ChevronDown, ExternalLink } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'

export default function DiscordPreview() {
  const [expanded, setExpanded] = useState(false)
  return (
    <figure
      aria-describedby="event-preview-caption"
      className="mx-auto w-full min-w-0 max-w-[560px]"
    >
      <div
        id="event-preview-content"
        className="rounded-xl border border-white/10 bg-[#0B1A1D] p-3 shadow-2xl shadow-black/20 tablet:p-4"
      >
        <div className="mb-2 flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#111214]">
            <Image
              src="/img/logos/314x314.webp"
              alt=""
              width={32}
              height={32}
              className="size-8 object-contain"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-[#F2F3F5]">
              Grid Finder
            </span>
            <span className="inline-flex items-center rounded-[3px] bg-[#5865F2] px-1 text-[10px] font-semibold leading-4 text-white">
              <Check aria-hidden="true" className="size-3" /> APP
            </span>
            <span className="ml-1 text-[10px] text-[#B5BAC1]">11:39 AM</span>
          </div>
        </div>
        <div className="overflow-hidden rounded-[6px] border border-l-4 border-white/10 border-l-primary bg-[#1C2B2E] px-3 py-3 text-[13px] leading-[1.5] text-[#DBDEE1] tablet:ml-[52px]">
          <p className="font-semibold text-[#82B5FF]">Example organiser</p>
          <p className="mb-3 mt-3 text-base font-bold text-[#82B5FF]">
            Round 1 | Example Circuit
          </p>
          <div
            className={`mb-2 flex flex-col justify-between gap-3 rounded-[3px] border border-primary bg-[radial-gradient(ellipse_at_top_right,rgba(255,97,0,1),transparent_100%)] p-4 ${expanded ? 'aspect-video tablet:p-5' : ''}`}
          >
            <Image
              src="/img/logos/1248x291.webp"
              alt="Grid Finder"
              width={1248}
              height={291}
              sizes="128px"
              className="h-auto w-32"
            />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-primary-200">
                Example Racing Community
              </p>
              <p
                className={`mt-1 font-bold leading-tight text-white ${expanded ? 'text-xl tablet:text-2xl' : 'text-lg'}`}
              >
                Community Time Trial Cup
              </p>
              <p hidden={!expanded} className="mt-2 text-xs text-slate-300">
                Your community. Your grid.
              </p>
            </div>
          </div>
          <dl>
            {[
              ['🏆', 'Championship', 'Community Time Trial Cup'],
              ['👥', 'Community', 'Example Racing Community'],
              ['🎮', 'Game', 'Your racing sim'],
              ['🏎️', 'Car Classes', 'GT3'],
              ['🏁', 'Track', 'Example Circuit'],
            ].map(([emoji, label, value]) => (
              <div
                key={label}
                hidden={!expanded && !['Game', 'Track'].includes(label)}
              >
                <dt className="inline font-bold">
                  <span aria-hidden="true">{emoji} </span>
                  {label}:{' '}
                </dt>
                <dd className="inline">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-2">
            <p className="font-bold">
              <span aria-hidden="true">🗓️ </span>Event Date &amp; Time
            </p>
            <p>
              <span className="rounded-[2px] bg-white/10 px-0.5">
                Sunday at 7:00 PM · Example time
              </span>
              <span hidden={!expanded} className="text-[#82B5FF]">
                {' '}
                - Add to Google Calendar
              </span>
            </p>
          </div>
          <dl hidden={!expanded} className="mt-2">
            <div>
              <dt className="inline font-bold">
                <span aria-hidden="true">⏱️ </span>Duration:{' '}
              </dt>
              <dd className="inline">7d</dd>
            </div>
            <div>
              <dt className="inline font-bold">
                <span aria-hidden="true">🔁 </span>Sessions:{' '}
              </dt>
              <dd className="inline">H</dd>
            </div>
          </dl>
          <p className="mt-2 font-bold">
            <span aria-hidden="true">🏎️ </span>Drivers (5 / 100)
          </p>
          <ul
            hidden={!expanded}
            className="mt-1 border-l-[3px] border-[#737A80] pl-3 text-[#B5BAC1]"
          >
            {[
              'Alex Racing',
              'Sam on Track',
              'Jordan GT',
              'Casey Motorsport',
              'Taylor Racing',
            ].map((driver) => (
              <li key={driver}>{driver}</li>
            ))}
          </ul>
          <p hidden={!expanded} className="mt-3">
            <strong>
              <span aria-hidden="true">🔧 </span>Reserves (0):
            </strong>{' '}
            -
          </p>
          <p hidden={!expanded} className="mt-5 text-[#82B5FF]">
            Grid Finder
          </p>
          <p hidden={!expanded} className="mt-2 text-xs">
            <span className="rounded-[2px] bg-white/10 px-0.5">
              5 seconds ago
            </span>
          </p>
          <div
            aria-hidden="true"
            className="mt-3 flex flex-wrap gap-1.5 border-t border-white/10 pt-2 text-[11px] font-semibold leading-5 text-white"
          >
            <span className="rounded-[6px] bg-[#008248] px-2 py-1">
              Register
            </span>
            <span className="rounded-[6px] bg-[#C92D3E] px-2 py-1">
              Withdraw
            </span>
            <span className="rounded-[6px] bg-[#2C3A3E] px-2 py-1">
              Reserve
            </span>
            <span className="rounded-[6px] bg-[#2C3A3E] px-2 py-1">
              🔁 Sync
            </span>
            <span className="inline-flex items-center gap-1 rounded-[6px] bg-[#2C3A3E] px-2 py-1">
              View Full Entry List <ExternalLink className="size-3" />
            </span>
          </div>
          <p className="sr-only">
            Example Discord controls: Register, Withdraw, Reserve, Sync and View
            Full Entry List. These controls are not interactive in this preview.
          </p>
        </div>
      </div>
      <div className="mt-2 flex justify-center">
        <Button
          type="button"
          variant="link"
          aria-expanded={expanded}
          aria-controls="event-preview-content"
          onClick={() => setExpanded((previous) => !previous)}
          className="min-h-11 gap-2 text-primary-200 focus-visible:ring-white dark:text-primary-200"
        >
          {expanded ? 'Show compact example' : 'View full example'}
          <ChevronDown
            aria-hidden="true"
            className={`size-4 ${expanded ? 'rotate-180' : ''}`}
          />
        </Button>
      </div>
      <figcaption
        id="event-preview-caption"
        className="mt-1 text-center text-xs leading-5 text-slate-400"
      >
        Example event embed · Fictional community and drivers
        <br />
        Controls shown for illustration.
      </figcaption>
    </figure>
  )
}
